import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { DialogBox, DialogBoxFooterType, Loader, LoaderTypes, TextArea, TextInput } from "~/core";
import "./send-rec-john-deere.css";
import { messages } from "../i18n-messages";
import * as selectors from "../../selectors";
import { useAppSelector } from "~/store/hooks";
import { getMessageForValidationFeedback } from "~/notifications/selectors";

interface ISendRecJohnDeereProps {
    onSend: (evt: { workOrder: string; instructions: string }) => void;
    onClose: () => void;
    sendDestination: string;
    isBatch: boolean;
}

interface IRollingMessageObject {
    id: number;
    step: number;
    message: string;
}

export default function SendRecJohnDeere(props: ISendRecJohnDeereProps): JSX.Element {
    const { isBatch, onSend, onClose, sendDestination } = props;
    const { formatMessage } = useIntl();
    const isLoading = useAppSelector(selectors.getLoading);
    const rollingMessage = useAppSelector(getMessageForValidationFeedback);

    const [workOrder, setWorOrder] = useState<string>("");
    const [instructions, setInstructions] = useState<string>("");
    const [disableSend, setDisableSend] = useState<boolean>(false);
    const [validationFeedback, setValidationFeedback] = useState<string>("");
    const [rollingMessageHolder, setRollingMessageHolder] = useState<IRollingMessageObject[]>([]);
    const [showLoader, setShowLoader] = useState<boolean>(false);

    useEffect(() => {
        if (rollingMessage && showLoader) {
            setShowLoader(false);
        }
        if (rollingMessage?.id == null) {
            const oldValidationFeedback = validationFeedback;
            const newValidationFeedback =
                oldValidationFeedback.length === 0
                    ? rollingMessage?.message
                    : oldValidationFeedback + "\n" + rollingMessage?.message;
            setValidationFeedback(newValidationFeedback);
        }
        if (rollingMessage?.id > 0) {
            setRollingMessageHolder([rollingMessage, ...rollingMessageHolder]);
            const rollingMessageObjects = [rollingMessage, ...rollingMessageHolder];
            const sortedRollingMessages = [...rollingMessageObjects]
                .sort((a, b) => a.step - b.step)
                .sort((c, d) => c.id - d.id)
                .map((x) => x.message);
            const newValidationFeedback = sortedRollingMessages.join("\n");
            setValidationFeedback(newValidationFeedback);
        }
    }, [rollingMessage]);

    useEffect(() => {
        setValidationFeedback("");
        setRollingMessageHolder([]);
    }, []);

    const onWorkPlanSend = () => {
        setShowLoader(true);
        onSend({
            workOrder,
            instructions,
        });
        setDisableSend(true);
    };

    return (
        <DialogBox
            action="Send"
            actionDisabled={disableSend}
            className={isBatch ? "validation-feedback-modal-batch" : "validation-feedback-modal"}
            draggable
            footerType={DialogBoxFooterType.ACTION_CLOSE}
            forceOverflow
            isOpen={true}
            onAction={onWorkPlanSend}
            onClose={onClose}
            title={`${formatMessage(
                isBatch ? messages.sendRecsToText : messages.sendRecToText
            )} ${sendDestination}`}
            unrestricted
        >
            {showLoader && <Loader />}
            <TextInput
                placeholderText={formatMessage(messages.workOrderText)}
                value={workOrder}
                onChange={(value) => setWorOrder(value)}
                maxLength={255}
            ></TextInput>
            <TextArea
                containerClassNames={["work-instructions"]}
                placeholderText={formatMessage(messages.instructionsText)}
                value={instructions}
                onChange={(value) => setInstructions(value)}
                maxLength={255}
            ></TextArea>
            <TextArea
                placeholderText={formatMessage(messages.statusMessagesText)}
                value={validationFeedback}
                readOnly
                disabled
            ></TextArea>
            {!isLoading ? null : <Loader type={LoaderTypes.LINE_SCALE_PULSE_OUT} />}
        </DialogBox>
    );
}
